<template>
  <div class="sent-gifts-container">
    <div class="sent-gifts-content">
      <div class="sent-gifts-header">
        <!-- タイトル -->
        <div class="sent-gifts-title">{{ $t('historiesTopSendedGift') }}</div>
      </div>

      <!-- ギフトリスト -->
      <div class="sent-gifts-list">
        <div 
          v-for="(gift, index) in giftList" 
          :key="index" 
          class="sent-gift-item"
        >
          <div class="sent-gift-item-icon">
            <img 
              :src="gift.icon" 
              :alt="gift.name" 
              class="sent-gift-icon-img"
            />
          </div>
          <div class="sent-gift-item-info">
            <div class="sent-gift-count">{{ $t('commonGiftAmount', { count: gift.count }) }}</div>
            <div class="sent-gift-name">{{ gift.name }}</div>
          </div>
        </div>
      </div>

      <!-- 区切り線 -->
      <div class="divider-line">
        <svg class="divider-svg" viewBox="0 0 311 2" fill="none">
          <path
            d="M0 1H311"
            stroke="white"
            stroke-width="1"
            opacity="0.5"
          />
        </svg>
      </div>

      <!-- 詳細ギフト -->
      <div class="detailed-sent-gifts">
        <div 
          v-for="(gift, index) in detailedGifts"
          :key="index"
          class="detailed-sent-gift-wrapper"
        >
          <DetailedGift :gift="gift" :recipient-label="$t('historiesTopSendGiftTo')" />
          <!-- 区切り線（最後以外） -->
          <div 
            v-if="index < detailedGifts.length - 1"
            class="divider-line"
          >
            <svg class="divider-svg" viewBox="0 0 311 2" fill="none">
              <path
                d="M0 1H311"
                stroke="white"
                stroke-width="1"
                opacity="0.5"
              />
            </svg>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Vue, Component, Prop } from 'vue-property-decorator'
import DetailedGift from './DetailedGift.vue'
import { HistoryAlbumKeyedData } from '@/api/pb/HistoryAlbum/Common/HistoryAlbumKeyedData'
import { HistoryAlbumUser } from '@/api/pb/HistoryAlbum/Common/HistoryAlbumUser'
import { HistoryAlbumSendGiftRecord } from '@/api/pb/HistoryAlbum/Common/HistoryAlbumSendGiftRecord'

@Component({
  components: {
    DetailedGift
  }
})
export default class SentGiftsSummary extends Vue {
  @Prop({ type: Number, required: true })
  year!: number

  // 年別データをpropsで受け取る
  @Prop({ type: Object, default: null })
  yearData!: HistoryAlbumKeyedData | null

  // ユーザー情報をpropsで受け取る
  @Prop({ type: Object, default: null })
  user!: HistoryAlbumUser | null

  // 簡易ギフトリスト（yeardataの構造体をそのまま使用）
  get giftList() {
    if (!this.yearData || !this.yearData.sendGiftRecords) {
      return []
    }

    return this.yearData.sendGiftRecords.map(record => ({
      icon: record.gift?.iconUrl || '',
      count: record.totalCount.toLocaleString(),
      name: record.gift?.name || ''
    }))
  }

  // 詳細ギフト情報（yeardataの構造体をそのまま使用）
  get detailedGifts(): HistoryAlbumSendGiftRecord[] {
    if (!this.yearData || !this.yearData.sendGiftRecords) {
      return []
    }

    // 送信したギフトレコードをそのまま返す
    return this.yearData.sendGiftRecords
  }
}
</script>

<style scoped>
.sent-gifts-container {
  position: relative;
  width: 100%;
  border-radius: 8px;
  background: linear-gradient(126deg, #FF7C91 0%, #E75EE0 100%);
  overflow: hidden;
}

/* 背景のGIFTテキスト（ピンク） */
.sent-gifts-container::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 220px;
  height: 50px;
  background-image: url('/img/campaign/reality_histories_20m/img_text_gift_pink.svg');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: left top;
  opacity: 1;
  z-index: 1;
}

/* 背景のギフトボックス（ピンク） */
.sent-gifts-container::after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 120px;
  height: 120px;
  background-image: url('/img/campaign/reality_histories_20m/img_gift_pink.svg');
  background-size: contain;
  background-repeat: no-repeat;
  background-position: right top;
  opacity: 1;
  z-index: 1;
}

.sent-gifts-content {
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 32px 16px 16px 16px;
  position: relative;
  width: 100%;
  z-index: 2;
}

/* ヘッダー */
.sent-gifts-header {
  position: relative;
  width: 100%;
}

.sent-gifts-title {
  font-weight: 600;
  font-style: normal;
  font-size: 26px;
  color: #ffffff;
  text-align: left;
  white-space: nowrap;
  line-height: normal;
  position: relative;
  z-index: 3;
}

/* ギフトリスト */
.sent-gifts-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: flex-start;
  justify-content: flex-start;
  padding: 0;
  position: relative;
  width: 100%;
}

.sent-gift-item {
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
  justify-content: flex-start;
  padding: 0;
  position: relative;
  width: 100%;
}

.sent-gift-item-icon {
  background: #ffffff;
  border-radius: 4px;
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sent-gift-icon-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.sent-gift-item-info {
  flex: 1;
  min-height: 1px;
  min-width: 1px;
  position: relative;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  line-height: 0;
  font-style: normal;
  padding: 0;
  color: #ffffff;
  text-align: left;
}

.sent-gift-count {
  font-weight: 300;
  position: relative;
  flex-shrink: 0;
  font-size: 11px;
  width: 100%;
  line-height: normal;
}

.sent-gift-name {
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  position: relative;
  flex-shrink: 0;
  font-size: 16px;
  white-space: nowrap;
  width: 100%;
  line-height: normal;
}

/* 区切り線 */
.divider-line {
  position: relative;
  flex-shrink: 0;
  width: 100%;
}

.divider-svg {
  display: block;
  width: 100%;
  height: 100%;
}

/* 詳細ギフト */
.detailed-sent-gifts {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
}

.detailed-sent-gift-wrapper {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
}
</style>
